import type { ReactNode } from 'react';

export interface ListRowProps {
  /** The line a client reads first: a session's name, an exercise's name. */
  title: ReactNode;
  /** The quieter line under it: a weekday, a prescription, a count. */
  meta?: ReactNode;
  /** A thumbnail, a marker, an avatar. Never the only carrier of meaning. */
  leading?: ReactNode;
  /** A control or a state. A chevron, a check, a badge. */
  trailing?: ReactNode;
  className?: string;
}

/**
 * One row of a list — a session in a week, an exercise in a session.
 *
 * Ported from the hi-fi's `.list-row`: a leading slot, a title with a quieter
 * line under it, a trailing slot, and a hairline between rows that the last row
 * does not draw. The phone bezel, the painted status bar and the canvas chrome
 * around it in that file are **not** application UI and are not here.
 *
 * It is the one primitive `src/ui` gains in this change, and that is deliberate.
 * Slice 1 proves buttons, cards, badges and the session list; the first three
 * already exist, so the list row is what is missing. Charts, sliders, message
 * bubbles, bottom navigation and sheets are not proven until slices 2 and 3, and
 * a primitive with no route is untested dead code (`client-app-direction`, D5).
 *
 * Three things about its shape are requirements rather than taste:
 *
 *   * **the title truncates, the row does not grow.** A long exercise name on a
 *     400 px phone has to fold gracefully rather than push the trailing control
 *     off the screen;
 *   * **`meta` is `ink-muted` on the page ground, measured at 5.60:1**, above
 *     the floor for normal text — a client reads this at arm's length in a
 *     bright gym, so the quiet line still has to be readable rather than
 *     decorative;
 *   * **the leading slot never carries meaning alone.** It is an illustration of
 *     something the words already say, because a row whose state is only a
 *     colour is a row half the readers cannot use.
 *
 * It is a `div`, not an `li`: the caller owns the list semantics, so the same row
 * works inside `<ul>`, inside `<ol>` where order matters, and on its own.
 */
export function ListRow({ title, meta, leading, trailing, className }: ListRowProps) {
  return (
    <div
      className={[
        'border-border flex items-center gap-3 border-b py-3 last:border-b-0',
        className ?? '',
      ].join(' ')}
    >
      {leading === undefined ? null : <div className="shrink-0">{leading}</div>}

      <div className="min-w-0 flex-1">
        <p className="text-ink truncate text-sm font-medium">{title}</p>
        {meta === undefined ? null : <p className="text-ink-muted mt-0.5 text-xs">{meta}</p>}
      </div>

      {trailing === undefined ? null : <div className="shrink-0">{trailing}</div>}
    </div>
  );
}
